import React, { useState, useEffect } from 'react';
import { useSwipeable } from 'react-swipeable';
import MonthDays from '../components/MonthDays';
import config from '../config';
import { themes } from '../config/themes';
import { useSearchParams } from 'react-router-dom';
import { generateEventSlug, parseEventSlug } from '../utils/eventUtils';
import OrganizationFilter from '../components/OrganizationFilter';

function EmbeddedHome() {

    const thisDate = new Date();   
    const thisMonth = thisDate.getMonth() + 1;
    const thisYear = thisDate.getFullYear();

  const [searchParams] = useSearchParams();
  const themeParam = searchParams.get('theme');
  const theme = themes[themeParam === 'dark' ? 'dark' : 'light'];

  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEventSlug, setSelectedEventSlug] = useState(window.location.hash.slice(1) || null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null);
  
  // Get current date
  const [curMonth, setCurMonth] = useState(thisMonth);
  const [curYear, setCurYear] = useState(thisYear);

  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('all');

  // Handle hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (hash) {
        const dateInfo = parseEventSlug(hash);
        if (dateInfo) {
          // Only update month/year if we're not already on that month
          if (dateInfo.month !== curMonth || dateInfo.year !== curYear) {
            setCurMonth(dateInfo.month);
            setCurYear(dateInfo.year);
          }
          setSelectedEventSlug(hash);
          setIsModalOpen(true);
        }
      } else {
        setSelectedEventSlug(null);
        setIsModalOpen(false);
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [curMonth, curYear]);

  // Handle modal state
  const handleModalClose = () => {
    //window.location.hash = '';
  };

  // Swipe handlers for mobile
  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (!isModalOpen) {
        setSwipeDirection('right');
        setIsAnimating(true);
        handleIncrement();
        setTimeout(() => {
          setIsAnimating(false);
          setSwipeDirection(null);
        }, 300);
      }
    },
    onSwipedRight: () => {
      if (!isModalOpen) {
        setSwipeDirection('left');
        setIsAnimating(true);
        handleDecrement();
        setTimeout(() => {
          setIsAnimating(false);
          setSwipeDirection(null);
        }, 300);
      }
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: false
  });

  const gotoToday = () => {
    setCurMonth(thisMonth);
    setCurYear(thisYear);
  }

  const handleIncrement = () => {
    setCurMonth(prevMonth => {
      if (prevMonth === 12) {
        setCurYear(prevYear => prevYear + 1);
        return 1;
      }
      return prevMonth + 1;
    });
  };

  const handleDecrement = () => {
    setCurMonth(prevMonth => {
      if (prevMonth === 1) {
        setCurYear(prevYear => prevYear - 1);
        return 12;
      }
      return prevMonth - 1;
    });
  };

  const renderMonthName = () => {
    const date = new Date(curYear, curMonth - 1, 1);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  // Fetch events for the current month
  useEffect(() => {
    const fetchEvents = async () => {
      setIsLoading(true);
      const cacheKey = `events-${curYear}-${curMonth}`;
      const versionKey = 'events-api-version';
      const cachedData = localStorage.getItem(cacheKey);
      const cachedVersion = localStorage.getItem(versionKey);
      
      try {
        // Check API version first
        const versionResponse = await fetch(`${config.apiBaseUrl}${config.endpoints.events.version}`);
        const currentVersion = await versionResponse.text();

        if (cachedVersion !== currentVersion) {
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('events-')) {
              localStorage.removeItem(key);
            }
          });
          localStorage.setItem(versionKey, currentVersion);
        } else if (cachedData) {
          setEvents(JSON.parse(cachedData));
          setIsLoading(false);
          setInitialLoadComplete(true);
          return;
        }

        const response = await fetch(`${config.apiBaseUrl}${config.endpoints.events.monthly}/${curYear}/${curMonth}`);
        const data = await response.json();
        localStorage.setItem(cacheKey, JSON.stringify(data));
        setEvents(data);
      } catch (error) {
        //console.error('Error fetching events:', error);
        if (cachedData) {
          setEvents(JSON.parse(cachedData));
        } else {
          setEvents([]);
        }
      } finally {
        setIsLoading(false);
        setInitialLoadComplete(true);
      }
    };

    fetchEvents();
  }, [curMonth, curYear]);

  // Extract unique organizations whenever events change
  useEffect(() => {
    if (events && events.length > 0) {
      const orgs = [...new Set(events.map(event => event.organization))].sort();
      setOrganizations(orgs);
    } else {
      setOrganizations([]);
    }
  }, [events]);

  // Filter events based on selected organization
  const filteredEvents = selectedOrg === 'all' 
    ? events 
    : events.filter(event => event.organization === selectedOrg);

  return (
    <div className={`container mx-auto px-1 py-2 max-w-7xl ${theme.background} ${theme.text}`}>
      {/* Month Navigation */}
      <div className="flex justify-between items-center mb-4 px-2">
        <h2 className="text-xl font-bold">
          {renderMonthName()}
        </h2>
        <div className="flex items-center">
          <img 
            src="/atl-tech-events-logo-transparent.png" 
            alt="ATL Tech Events Logo" 
            className="h-8 md:h-10" 
            aria-label="ATL Tech Events"
          />
        </div>
        <div className="flex items-center gap-4">

          <button
            onClick={handleDecrement}
            className={`${theme.secondaryText} ${theme.buttonHover}`}
            aria-label="Previous month"
          >
            ←
          </button>
          <button
            onClick={gotoToday}
            className={`${theme.secondaryText} ${theme.buttonHover}`}
            aria-label="Go to today"
          >
            Today
          </button>
          <button
            onClick={handleIncrement}
            className={`${theme.secondaryText} ${theme.buttonHover}`}
            aria-label="Next month"
          >
            →
          </button>
        </div>
      </div>

      {/* Add organization filter */}
      <div className={`rounded-lg shadow-md opacity-[.93] ${theme.gridBg} p-4 mb-4`}>
        <OrganizationFilter
          organizations={organizations}
          selectedOrg={selectedOrg}
          onOrgChange={setSelectedOrg}
          theme={theme}
        />
      </div>

      {/* Calendar Grid */}
      <div className="w-full relative" {...handlers}>
        {isLoading && !initialLoadComplete && (
          <div className={`fixed inset-0 ${theme.modalOverlay} flex items-center justify-center`}>
            <div className={`${theme.loadingBg} ${theme.loadingText} p-4 rounded-lg`}>
              Loading events...
            </div>
          </div>
        )}

        <div 
          className={`transition-transform duration-300 ease-in-out ${
            isAnimating
              ? swipeDirection === 'left'
                ? 'translate-x-[-100%] opacity-0'
                : 'translate-x-[100%] opacity-0'
              : 'translate-x-0 opacity-100 w-full relative'
          }`}
          role="region"
          aria-label="Calendar view"
        >
          <MonthDays 
            curMonth={curMonth} 
            curYear={curYear} 
            events={filteredEvents}
            onModalStateChange={setIsModalOpen}
            selectedEventSlug={selectedEventSlug}
            onModalClose={handleModalClose}
            isModalOpen={isModalOpen}
            theme={theme}
          />
        </div>
      </div>

      {/* Powered By Footer */}
      <div className="text-center mt-4 text-sm">
        <a 
          href="https://atltech.events" 
          target="_blank" 
          rel="noopener noreferrer"
          className={theme.link}
        >
          Powered by ATLTech.Events
        </a>
      </div>
    </div>
  );
}

export default EmbeddedHome;